// /settings/streak — настройки серии обучения: дневная цель, время напоминания,
// авто-заморозка. Реальный backend через tRPC streaks.updateSettings (хук
// use-streak). Часовой пояс — read-only (хранится в users.timezone, меняется в
// другом потоке; updateSettings его игнорирует).
//
// Источник истины состояния — streaks.getState (server-canonical агрегат),
// поэтому не PowerSync, а tRPC (см. components/streaks/_api.ts шапку).

import { Combobox, Switch } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconBell, IconFlame, IconSnowflake, IconTarget } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import {
	SettingsFieldRow,
	SettingsRow,
	SettingsSection,
} from "@/components/settings/settings-shell";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useStreak } from "@/hooks/use-streak";

export const Route = createFileRoute("/settings/streak")({
	component: StreakSettingsRoute,
	pendingComponent: PagePendingFallback,
});

// Контракт updateSettingsInput требует dailyGoalMinutes > 0 (0 не принимается).
const GOAL_OPTIONS_MIN = [10, 15, 20, 30, 45, 60, 90, 120] as const;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);

function StreakSettingsRoute() {
	const { t } = useTranslation("settings");
	const { state, isLoading, updateSettings } = useStreak();

	const [goal, setGoal] = useState(30);
	const [hour, setHour] = useState(20);
	const [autoFreeze, setAutoFreeze] = useState(true);
	const hydrated = useRef(false);

	// Гидратация реальными значениями стрика (а не дефолтами).
	useEffect(() => {
		if (!state || hydrated.current) return;
		hydrated.current = true;
		setGoal(state.dailyGoalMinutes);
		setHour(state.nudgeHourLocal);
		setAutoFreeze(state.autoFreezeEnabled);
	}, [state]);

	function persist(patch: {
		dailyGoalMinutes?: number;
		nudgeHourLocal?: number;
		autoFreezeEnabled?: boolean;
	}) {
		updateSettings.mutate(patch, {
			onError: () => toast.error(t("streak.saveError")),
		});
	}

	if (isLoading || !state) return <PagePendingFallback />;

	const goalOptions = GOAL_OPTIONS_MIN.map((m) => ({
		value: String(m),
		label: t("streak.goalOption", { minutes: m }),
	}));
	const hourOptions = HOUR_OPTIONS.map((h) => ({
		value: String(h),
		label: t("streak.reminderOption", { hour: String(h).padStart(2, "0") }),
	}));

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("streak.title")}
				description={t("streak.subtitle")}
				icon={<IconFlame stroke={1.75} />}
				category="streak"
			/>

			<SettingsSection title={t("streak.goalSection")} icon={<IconTarget stroke={1.75} />}>
				<SettingsFieldRow label={t("streak.goalLabel")} description={t("streak.goalHelp")}>
					<Combobox
						options={goalOptions}
						value={String(goal)}
						onChange={(v) => {
							const next = Number(v);
							setGoal(next);
							persist({ dailyGoalMinutes: next });
						}}
						placeholder={t("streak.goalLabel")}
					/>
				</SettingsFieldRow>
			</SettingsSection>

			<SettingsSection title={t("streak.reminderSection")} icon={<IconBell stroke={1.75} />}>
				<SettingsFieldRow label={t("streak.reminderLabel")} description={t("streak.reminderHelp")}>
					<Combobox
						options={hourOptions}
						value={String(hour)}
						onChange={(v) => {
							const next = Number(v);
							setHour(next);
							persist({ nudgeHourLocal: next });
						}}
						placeholder={t("streak.reminderLabel")}
					/>
				</SettingsFieldRow>
				<div className="px-4 py-2.5 text-xs text-muted-foreground">
					{t("streak.tzNote", { tz: state.timezone })}
				</div>
			</SettingsSection>

			<SettingsSection title={t("streak.freezeSection")} icon={<IconSnowflake stroke={1.75} />}>
				<SettingsRow
					label={t("streak.autoFreezeLabel")}
					description={t("streak.autoFreezeHelp")}
					icon={<IconSnowflake />}
				>
					<Switch
						checked={autoFreeze}
						onCheckedChange={(v) => {
							setAutoFreeze(v);
							persist({ autoFreezeEnabled: v });
						}}
					/>
				</SettingsRow>
			</SettingsSection>
		</SettingsPageContainer>
	);
}
